import React, { useMemo, useState } from 'react';
import Icon from '@/components/AppIcon';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Button from '@/components/ui/Button';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';

const SearchAndFilters = ({ 
  searchTerm, 
  onSearchChange, 
  filters, 
  onFiltersChange, 
  onClearFilters,
  employeeCount = 0,
  locations = [],
  roles = []
}) => {
  const { t } = useTranslation(['employeeDirectory', 'common']);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  const statusOptions = useMemo(() => {
    const statuses = [
      { value: 'on-work', key: 'onWork', fallback: 'On Work' },
      { value: 'off-work', key: 'offWork', fallback: 'Off Work' },
      { value: 'in-vacation', key: 'inVacation', fallback: 'In Vacation' },
      { value: 'holiday', key: 'holiday', fallback: 'Holiday' },
      { value: 'sick-leave', key: 'sickLeave', fallback: 'Sick Leave' },
    ];
    return [
      {
        value: '',
        label: t('employeeDirectory:filters.status.all', 'All Statuses'),
      },
      ...statuses.map(({ value, key, fallback }) => ({
        value,
        label: t(`employeeDirectory:statuses.${key}`, fallback),
      })),
    ];
  }, [t]);

  const locationOptions = useMemo(() => [
    {
      value: '',
      label: t('employeeDirectory:filters.locations.all', 'All Locations'),
    },
    ...(locations?.map((loc) => ({ value: loc, label: loc })) ?? []),
  ], [locations, t]);

  const roleOptions = useMemo(() => [
    {
      value: '',
      label: t('employeeDirectory:filters.roles.all', 'All Roles'),
    },
    ...(roles?.map((role) => ({ value: role, label: role })) ?? []),
  ], [roles, t]);

  const handleFilterChange = (key, value) => {
    onFiltersChange({
      ...filters,
      [key]: value
    });
  };

  const handleManagerQuickFilter = () => {
    const isActive = filters?.role === 'Manager';

    onFiltersChange({
      ...filters,
      role: isActive ? '' : 'Manager',
      department:
        !isActive && (filters?.department === 'Kitchen' || filters?.department === 'Service')
          ? ''
          : filters?.department
    });
  };

  const handleKitchenQuickFilter = () => {
    const isActive = filters?.department === 'Kitchen';

    onFiltersChange({
      ...filters,
      department: isActive ? '' : 'Kitchen',
      role: !isActive && filters?.role === 'Manager' ? '' : filters?.role
    });
  };

  const handleServiceQuickFilter = () => {
    const isActive = filters?.department === 'Service';

    onFiltersChange({
      ...filters,
      department: isActive ? '' : 'Service',
      role: !isActive && filters?.role === 'Manager' ? '' : filters?.role
    });
  };

  const activeFilterCount = Object.values(filters ?? {}).filter((value) => value).length;
  const hasActiveFilters = activeFilterCount > 0;

  const resultLabel = formatTranslation(
    t(
      `employeeDirectory:filters.results.${employeeCount === 1 ? 'singular' : 'plural'}`,
      employeeCount === 1 ? '{{count}} employee found' : '{{count}} employees found',
    ),
    { count: employeeCount ?? 0 },
  );

  const quickFilters = {
    onWork: t('employeeDirectory:filters.quick.onWork', 'On Work'),
    managers: t('employeeDirectory:filters.quick.managers', 'Managers'),
    kitchen: t('employeeDirectory:filters.quick.kitchen', 'Kitchen Staff'),
    service: t('employeeDirectory:filters.quick.service', 'Service Staff'),
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 space-y-4">
      {/* Search Bar */}
      <div className="flex items-center space-x-4">
        <div className="flex-1">
          <Input
            type="search"
            placeholder={t(
              'employeeDirectory:filters.searchPlaceholder',
              'Search employees by name, role, or department...',
            )}
            value={searchTerm}
            onChange={(e) => onSearchChange(e?.target?.value)}
            className="w-full"
          />
        </div>
        <Button
          variant="outline"
          onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
          className="flex items-center space-x-2"
        >
          <Icon name="Filter" size={16} />
          <span className="hidden sm:inline">
            {t('employeeDirectory:filters.toggle', 'Filters')}
          </span>
          {hasActiveFilters && (
            <span className="bg-primary text-primary-foreground text-xs rounded-full px-2 py-1 ml-2">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </div>
      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{resultLabel}</span>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearFilters}
            className="text-xs"
          >
            <Icon name="X" size={14} className="mr-1" />
            {t('employeeDirectory:filters.clearFilters', 'Clear filters')}
          </Button>
        )}
      </div>
      {/* Filters Panel */}
      {isFiltersExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-border">
          <Select
            label={t('employeeDirectory:filters.labels.location', 'Location')}
            options={locationOptions}
            value={filters?.location || ''}
            onChange={(value) => handleFilterChange('location', value)}
          />
          
          <Select
            label={t('employeeDirectory:filters.labels.role', 'Role')}
            options={roleOptions}
            value={filters?.role || ''}
            onChange={(value) => handleFilterChange('role', value)}
          />
          
          <Select
            label={t('employeeDirectory:filters.labels.status', 'Status')}
            options={statusOptions}
            value={filters?.status || ''}
            onChange={(value) => handleFilterChange('status', value)}
          />
        </div>
      )}
      {/* Quick Filter Buttons */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filters?.status === 'on-work' ? 'default' : 'outline'}
          size="sm"
          onClick={() => handleFilterChange('status', filters?.status === 'on-work' ? '' : 'on-work')}
        >
          <div className="w-2 h-2 bg-success rounded-full mr-2"></div>
          {quickFilters.onWork}
        </Button>
        <Button
          variant={filters?.role === 'Manager' ? 'default' : 'outline'}
          size="sm"
          onClick={handleManagerQuickFilter}
        >
          <Icon name="Crown" size={14} className="mr-2" />
          {quickFilters.managers}
        </Button>
        <Button
          variant={filters?.department === 'Kitchen' ? 'default' : 'outline'}
          size="sm"
          onClick={handleKitchenQuickFilter}
        >
          <Icon name="ChefHat" size={14} className="mr-2" />
          {quickFilters.kitchen}
        </Button>
        <Button
          variant={filters?.department === 'Service' ? 'default' : 'outline'}
          size="sm"
          onClick={handleServiceQuickFilter}
        >
          <Icon name="Users" size={14} className="mr-2" />
          {quickFilters.service}
        </Button>
      </div>
    </div>
  );
};

export default SearchAndFilters;
